import React, { useEffect } from 'react';
import { useAuthStore } from '../store/authUserStore';
import { Layout, Typography, Card } from 'antd';
import { CalendarOutlined, CheckCircleOutlined, ClockCircleOutlined, TeamOutlined, InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import useEventStore from '../store/eventStore';
import useCustomerStore from '../store/customerStore';
import { useProductStore } from '../store/productStore';
import './Dashboard.css';

const { Content } = Layout;

const Dashboard: React.FC = () => {
  const currentUser = useAuthStore((state) => state.currentUser);
  const { t } = useTranslation();
  const { items: events, fetchItems: fetchEvents } = useEventStore();
  const { items: customers, fetchItems: fetchCustomers } = useCustomerStore();
  const { items: products, fetchItems: fetchProducts } = useProductStore();

  useEffect(() => {
    fetchEvents();
    fetchCustomers();
    fetchProducts();
  }, [fetchEvents, fetchCustomers, fetchProducts]);

  const pendingCount = events.filter(e => e.status?.toLowerCase() === 'pending').length;
  const finishedCount = events.filter(e => e.status?.toLowerCase() === 'finished').length;

  const stats = [
    {
      title: t('dashboard.totalEvents'),
      value: events.length,
      icon: <CalendarOutlined style={{ fontSize: 28, color: '#1677ff' }} />,
      accentClass: 'dashboard-stat-blue',
    },
    {
      title: t('dashboard.pendingEvents'),
      value: pendingCount,
      icon: <ClockCircleOutlined style={{ fontSize: 28, color: '#fa8c16' }} />,
      accentClass: 'dashboard-stat-orange',
    },
    {
      title: t('dashboard.finishedEvents'),
      value: finishedCount,
      icon: <CheckCircleOutlined style={{ fontSize: 28, color: '#52c41a' }} />,
      accentClass: 'dashboard-stat-green',
    },
    {
      title: t('dashboard.totalCustomers'),
      value: customers.length,
      icon: <TeamOutlined style={{ fontSize: 28, color: '#722ed1' }} />,
      accentClass: 'dashboard-stat-violet',
    },
    {
      title: t('dashboard.totalProducts'),
      value: products.length,
      icon: <InboxOutlined style={{ fontSize: 28, color: '#13c2c2' }} />,
      accentClass: 'dashboard-stat-cyan',
    },
  ];

  return (
    <Layout className="dashboard-layout">
      <Content className="dashboard-content">
        <div className="dashboard-shell">
          <div className="dashboard-banner">
            <Typography.Title level={3} className="dashboard-banner-title">
              {t('dashboard.welcome')}{currentUser ? `, ${currentUser.username}` : ''}!
            </Typography.Title>
            <Typography.Text className="dashboard-banner-subtitle">
              {t('general.eventManager')}
            </Typography.Text>
            {currentUser && (
              <span className="dashboard-role-badge">
                {currentUser.role}
              </span>
            )}
            <div className="dashboard-status-strip">
              <span className="dashboard-status-pill pending">
                {t('dashboard.pendingEvents')}: {pendingCount}
              </span>
              <span className="dashboard-status-pill finished">
                {t('dashboard.finishedEvents')}: {finishedCount}
              </span>
            </div>
          </div>

          <div className="dashboard-overview">
            <Typography.Title level={5} className="dashboard-overview-title">
              {t('dashboard.statistics')}
            </Typography.Title>
            <div className="dashboard-stats-grid">
              {stats.map((stat) => (
                <Card
                  key={stat.title}
                  className={`dashboard-stat-card ${stat.accentClass}`}
                  bodyStyle={{ padding: '16px 18px' }}
                >
                  <div className="dashboard-stat-head">
                    <div className="dashboard-stat-icon">{stat.icon}</div>
                    <div className="dashboard-stat-value">{stat.value}</div>
                  </div>
                  <Typography.Text className="dashboard-stat-title">{stat.title}</Typography.Text>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default Dashboard;
